import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import EmailIcon from '@mui/icons-material/Email'
import LoginIcon from '@mui/icons-material/Login'
import Swal from 'sweetalert2'
import { Oval } from 'react-loader-spinner'

import { requestUnlock } from 'service/TwoFactorService'

const AccountLockedPage = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  const handleResend = async () => {
    setError('')
    setLoading(true)
    try {
      await requestUnlock(email || undefined)
      setSent(true)
      Swal.fire({
        title: t('common.success'),
        text: t('auth.unlock_email_sent'),
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
      })
    } catch (e) {
      const msg = e?.response?.data?.detail || t('auth.letter_send_failed')
      setError(msg)
      Swal.fire({ title: t('common.error'), text: msg, icon: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Stack spacing={3} alignItems="center">
          <Box sx={{ bgcolor: '#FFF1E6', p: 2, borderRadius: '50%' }}>
            <LockIcon sx={{ fontSize: 56, color: '#FF7A00' }} />
          </Box>
          <Typography variant="h5" fontWeight={700} textAlign="center">
            {t('auth.account_locked_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {t('auth.account_locked_desc')}
          </Typography>

          {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
          {sent && <Alert severity="success" sx={{ width: '100%' }}>{t('auth.letter_sent')}</Alert>}

          <TextField
            fullWidth
            type="email"
            label={t('auth.email_optional')}
            placeholder={t('auth.email_placeholder_hint')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={loading ? <Oval height={18} width={18} color="#fff" secondaryColor="#ffe0c2" /> : <EmailIcon />}
              onClick={handleResend}
              disabled={loading}
              sx={{ bgcolor: '#FF7A00', '&:hover': { bgcolor: '#e96d00' } }}
            >
              {t('auth.request_unlock_email')}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/')}
              disabled={loading}
            >
              {t('auth.login_again')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  )
}

export default AccountLockedPage
